import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTripPlan } from "@/hooks/useTripPlan";
import { brand } from "@/mocks/siteData";
import BrandLogo from "@/components/feature/BrandLogo";
import Seo from "@/components/feature/Seo";
import {
  buildItineraryText,
  buildPoolLookup,
  collectStopLocations,
  formatRupees,
  pickMapCenter,
  summarizeDay,
  summarizeItinerary,
} from "@/pages/plan-trip/planUtils";

const typeLabels: Record<string, string> = {
  experience: "Experience",
  farm: "Farm",
  culture: "Konaseema",
};

export default function TripPrint() {
  const { plan, dayCount, totalStops } = useTripPlan();
  const [copied, setCopied] = useState(false);

  const lookup = useMemo(() => buildPoolLookup(), []);
  const summary = useMemo(() => summarizeItinerary(plan, dayCount), [plan, dayCount]);
  const itineraryText = useMemo(() => buildItineraryText(plan, dayCount), [plan, dayCount]);

  const days = useMemo(
    () =>
      Array.from({ length: dayCount }, (_, d) => ({
        index: d,
        items: (plan[d] || []).map((id) => lookup[id]).filter(Boolean),
        summary: summarizeDay(plan, d),
      })),
    [plan, dayCount, lookup],
  );

  const stopLocations = useMemo(() => collectStopLocations(plan, dayCount), [plan, dayCount]);
  const mapSrc = useMemo(
    () => `https://www.google.com/maps?q=${encodeURIComponent(pickMapCenter(plan, dayCount))}&z=10&output=embed`,
    [plan, dayCount],
  );

  useEffect(() => {
    if (totalStops === 0) return undefined;
    const timer = window.setTimeout(() => window.print(), 700);
    return () => window.clearTimeout(timer);
  }, [totalStops]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(itineraryText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const today = new Date().toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="print-page min-h-screen bg-background-100 py-8 md:py-12 px-4">
      <Seo
        title={`Your Custom Itinerary | ${brand.name}`}
        description="A ready-to-print farm and village itinerary for Konaseema, built with the Rythugaru trip planner."
        path="/plan-your-trip/print"
        noindex
      />
      <div className="mx-auto w-full max-w-3xl">
        <div className="no-print flex flex-wrap items-center justify-between gap-3 mb-6">
          <Link
            to="/plan-your-trip"
            className="inline-flex items-center gap-2 text-sm font-semibold text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-base"></i>
            Back to the planner
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center gap-2 text-sm font-semibold text-foreground-800 bg-background-50 border border-background-300 px-4 py-2.5 rounded-md hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className={copied ? "ri-check-line text-base" : "ri-file-copy-line text-base"}></i>
              {copied ? "Copied to clipboard" : "Copy as text"}
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 text-sm font-semibold text-background-50 bg-accent-500 px-4 py-2.5 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-printer-line text-base"></i>
              Print / Save as PDF
            </button>
          </div>
        </div>

        {totalStops === 0 ? (
          <div className="bg-background-50 rounded-lg border border-background-200 p-10 md:p-14 text-center">
            <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
              <i className="ri-route-line text-2xl"></i>
            </span>
            <h1 className="font-heading text-2xl text-foreground-950 font-semibold mt-5">
              Your itinerary is still empty
            </h1>
            <p className="text-sm text-foreground-600 leading-relaxed mt-3 max-w-md mx-auto">
              Add a few experiences, farms or Konaseema stops in the planner and a ready-to-print
              itinerary will appear right here.
            </p>
            <Link
              to="/plan-your-trip"
              className="mt-6 inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-5 py-3 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-line text-base"></i>
              Build your itinerary
            </Link>
          </div>
        ) : (
          <article className="print-sheet bg-background-50 rounded-lg border border-background-200 p-8 md:p-10">
            <header className="flex flex-wrap items-start justify-between gap-4 border-b border-background-200 pb-6">
              <BrandLogo imgClassName="h-12" />
              <div className="text-right">
                <p className="text-xs font-semibold uppercase tracking-wider text-accent-600">
                  Custom Itinerary
                </p>
                <p className="text-xs text-foreground-500 mt-1">Prepared {today}</p>
              </div>
            </header>

            <section className="grid grid-cols-2 sm:grid-cols-4 gap-4 py-6 border-b border-background-200">
              <div>
                <p className="text-xs text-foreground-500">Stops</p>
                <p className="font-heading text-2xl text-foreground-950 font-semibold mt-1">
                  {summary.stops}
                </p>
              </div>
              <div>
                <p className="text-xs text-foreground-500">Days</p>
                <p className="font-heading text-2xl text-foreground-950 font-semibold mt-1">
                  {summary.days}
                </p>
              </div>
              <div>
                <p className="text-xs text-foreground-500">Est. time</p>
                <p className="font-heading text-2xl text-foreground-950 font-semibold mt-1">
                  {summary.hours} hrs
                </p>
              </div>
              <div>
                <p className="text-xs text-foreground-500">Rough budget</p>
                <p className="font-heading text-2xl text-accent-700 font-semibold mt-1">
                  {formatRupees(summary.cost)}
                </p>
              </div>
            </section>

            {stopLocations.length > 0 && (
              <section className="mt-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-accent-600">
                    <i className="ri-map-pin-2-line"></i>
                    Where your stops are
                  </span>
                  <span className="h-px flex-1 bg-background-200"></span>
                  <span className="text-xs text-foreground-500">
                    {stopLocations.length} {stopLocations.length === 1 ? "area" : "areas"}
                  </span>
                </div>
                <div className="rounded-md overflow-hidden border border-background-200">
                  <iframe
                    title="Map of your selected stops"
                    src={mapSrc}
                    className="w-full h-52 md:h-64"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {stopLocations.map((location) => (
                    <span
                      key={location.query}
                      className="inline-flex items-center gap-1.5 text-xs font-medium bg-secondary-100 text-secondary-900 border border-secondary-200 px-3 py-1.5 rounded-full"
                    >
                      <i className="ri-map-pin-line text-secondary-700"></i>
                      {location.label}
                    </span>
                  ))}
                </div>
              </section>
            )}

            <section className="mt-6 space-y-7">
              {days.map((day) => (
                <div key={day.index}>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="inline-flex items-center justify-center text-xs font-bold text-background-50 bg-primary-500 px-3 py-1.5 rounded-full whitespace-nowrap">
                      Day {day.index + 1}
                    </span>
                    <span className="h-px flex-1 bg-background-200"></span>
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-foreground-500">
                      <span>
                        {day.items.length} {day.items.length === 1 ? "stop" : "stops"}
                      </span>
                      {day.summary.hours > 0 && (
                        <>
                          <span className="text-foreground-300">·</span>
                          <span>{day.summary.hours} hrs</span>
                        </>
                      )}
                      {day.summary.cost > 0 && (
                        <>
                          <span className="text-foreground-300">·</span>
                          <span className="font-semibold text-accent-700">
                            {formatRupees(day.summary.cost)}
                          </span>
                        </>
                      )}
                    </span>
                  </div>

                  {day.items.length === 0 ? (
                    <p className="text-sm text-foreground-500 italic pl-1">
                      No stops added to this day yet.
                    </p>
                  ) : (
                    <ul className="space-y-3">
                      {day.items.map((item) => (
                        <li
                          key={item.id}
                          className="flex items-start gap-3 border border-background-200 rounded-md px-4 py-3"
                        >
                          <span className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 shrink-0">
                            <i className={`${item.icon} text-lg`}></i>
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                              <p className="font-heading text-sm text-foreground-950 font-semibold">
                                {item.title}
                              </p>
                              <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground-400">
                                {typeLabels[item.type]}
                              </span>
                            </div>
                            <p className="text-xs text-foreground-600 mt-1 leading-relaxed">
                              {item.meta}
                            </p>
                          </div>
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground-700 whitespace-nowrap shrink-0">
                            <i className="ri-time-line text-accent-600"></i>
                            {item.duration}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </section>

            <footer className="mt-8 pt-6 border-t border-background-200">
              <p className="text-xs text-foreground-600 leading-relaxed">
                This is a rough budget estimate based on the time each stop usually takes — your
                final quote is confirmed with our team once dates and group size are set. Bring this
                page along or send it to us and we'll refine the details together.
              </p>
              <p className="text-xs text-foreground-400 mt-3">
                Planned with {brand.name} · Konaseema, Andhra Pradesh
              </p>
            </footer>
          </article>
        )}
      </div>
    </div>
  );
}